import * as Backend from './backend';
import { NonNullableObject } from './utils';

import { APIServerError } from 'utils/sra/types';

// generic
export enum APISortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

export type APIGenericList<ItemType> = {
  result: ItemType[];
  pageNumber: number;
  pageSize: number;
  count: number;
};

export type APIResponse<PayloadT> =
  | { error?: false; payload: PayloadT }
  | APIServerError;

export type APITypeaheadPayeeFilter = Backend.APITypeaheadPayeeFilter;

// app module
export type APIOrganization = Required<
  NonNullableObject<Backend.APIOrganizationModel>
>;
export type APIUserRegister = Backend.APIUserRegisterModel;
export type APIUserOrganization = Backend.APIUserOrganizationModel & {
  isSelected: boolean;
};

export { APIUserRight } from './backend';
export type APIUserRightRequest = Backend.APIUserRightsRequestModel;
export type APIUserInvite = Backend.APIUserInviteModel;

export enum PrintType {
  Check = 'check',
  WhitePaper = 'whitepaper',
}

// user module
export {
  APIPayeeStatus,
  APIPaymentStatus,
  APIUserResetPasswordModel,
  APIUserSettingModel,
  APIUserSettingType,
  APIUserStatus,
  APIUserType,
} from './backend';

// setting module
export {
  APINumericOperators,
  APISettingName,
  APIStringOperators,
} from './backend';
export type APISetting = Backend.APISettingModel;

// organization module
export type APIOrganizationSearchResult = APIGenericList<APIOrganization>;
export type APIOrganizationSearchFilter =
  Backend.APIOrganizationSearchFilter;

// payee module
export type APIPayee = Required<NonNullableObject<Backend.APIPayeeModel>>;
export type APIPayeeRequestModel = Required<
  NonNullableObject<Backend.APIPayeeRequestModel>
>;
export type APIPayeeSearchResult = APIGenericList<APIPayee>;
export type APIPayeeSearchFilter = Backend.APIPayeeSearchFilter;

// payments module
export type APIBank = Required<NonNullableObject<Backend.APIBankModel>>;
export type APIBankSearchResult = APIGenericList<APIBank>;
export type APIBankSearchFilter = Backend.APIBankSearchFilter;
export type APIPaymentEmailStatus = Required<
  NonNullableObject<Backend.APIPaymentEmailStatus>
>;

export type APIPaymentRequest = Required<
  NonNullableObject<Backend.APIPaymentRequestModel>
>;
export type APIPayment = Required<
  NonNullableObject<Backend.APIPaymentModel>
>;
export type APIPaymentSearchResult = APIGenericList<APIPayment>;
export type APIPaymentSearchFilter = Backend.APIPaymentSearchFilter;
export type APIPaymentUserRequest = Backend.APIPaymentUserRequestModel;

// Filter types
export type APIStringSearchFilter = Backend.APIStringSearchFilter;
export type APIDecimalSearchFilter = Backend.APIDecimalSearchFilter;
export type APIDateOnlySearchFilter = Backend.APIDateOnlySearchFilter;
export type APIDateTimeSearchFilter = Backend.APIDateTimeSearchFilter;

// user module
export type APIUser = Omit<
  Required<Backend.APIUserModel>,
  'userOrganizations'
> & {
  userOrganizations: APIUserOrganization[];
};

export type APIUserSearchResult = APIGenericList<APIUser>;
export type APIUserSearchFilter = Backend.APIUserSearchFilter;

// audit module
export type APIAudit = Required<NonNullableObject<Backend.APIAuditModel>>;
export type APIAuditSearchFilter = Backend.APIAuditSearchFilter;
export type APIAuditSearchResult = APIGenericList<APIAudit>;

// api client token module
export type APIApiClientToken = Required<
  NonNullableObject<Backend.APIApiClientTokenModel>
>;

export type APIApiClientTokenSearchFilter =
  Backend.APIApiClientTokenSearchFilter & { isEnabled?: boolean };

export type APIApiClientTokenCreateModel =
  Backend.APIApiClientTokenCreateModel;

export type APIApiClientTokenToggleModel = {
  isEnabled: boolean;
};

export type APIApiClientTokenSearchResult = APIGenericList<APIApiClientToken>;

// blank check module
export type APIBlankCheckRequest = {
  checkNumberFrom: string;
  checkNumberTo: string;
  withSignature: boolean;
  printType: PrintType;
};

export type APIBlankCheck = {
  id: string;
  bankId: string;
  checkNumberFrom: string;
  checkNumberTo: string;
  withSignature: boolean;
  printType: PrintType;
  createdOn: string;
  createdBy: { id: string; firstName: string; lastName: string; email: string } | null;
};

